// 花嫁「誓いの抱擁」が当たったら少しの間抱きとめて放さない (2026-10-27・配布版の既定。検収 kensyu-bride-hugbind-1027 で作者「配布版に」)。
//   開発版 ?brideHug=old で前の配布版 (抱きとめず、抱擁のあとに返しの一閃が続く)。
//   作者 (検収 kensyu-bride-1023b の H-2 メモ)「抱きしめがあたったら、少しの間拘束して。合わせてハートのエフェクトを出して」
//   作り:
//     当たると主人公を腕の中で 0.65 秒止める (入力は効かない)。傷は放す時に一度だけ (今の抱擁と同じ量)。
//     放す時は軽く押し離す。抱いている間は♡が花嫁のあごの下 (胸もと) から3つふわっと浮いて消える (桜色と銀白・輪郭線なし・赤は使わない)。
//     連「抱擁 → 返しの一閃」は、抱きとめたら一閃を捨てて戻りへ (GrabSpec.endsString・src/boss.ts の拍の間)。
//       捨てないと、放した 0.07 秒ほど後に一閃が届き、弾くのも転がるのも間に合わない確定の追撃になる。
//     抱いている間は前へ手を伸ばす寄り (recoveryDriftPx) を止める (腕が主人公を通り抜けないように)。
//   長さ・抜け方の理由 = docs/research/2026-10-27-bride-hug-bind.md
// 配布版は URL を読まない (import.meta.env.DEV の時だけ old を受ける)。⚠️ 識別子に URL のキーの綴りを入れない (scripts/check_dev_leak.mjs)。
import { ATTACKS } from './attacks'
import { PLAYER_DAMAGE } from './balance'
import { drawHeart } from './exec_unique_fx'
import { projectBossSpriteRelativePoint, type BossSpritePlacement } from './sprites'
import type { GrabSpec, Player } from './types'

/** 抱く腕の中で止める所 (花嫁の足元から前)。拘束しない抱擁が当たった瞬間の主人公の位置に合わせた (撮影で測った)。 */
export const BRIDE_HUG_HOLD_FWD_PX = 51
export const BRIDE_HUG_GRAB: GrabSpec = {
  holdMs: 650,
  damage: PLAYER_DAMAGE.standard,
  releaseKnockbackPx: 70,
  pullMs: 120,
  anchorFwdPx: BRIDE_HUG_HOLD_FWD_PX,
  endsString: true,
}

// ♡: 抱いた瞬間から 0・130・260ms に1つずつ生まれ、0.9 秒かけて 46px 浮いて消える。
export const BRIDE_HUG_HEART = { birthMs: [0, 130, 260], lifeMs: 900, risePx: 46, sizePx: 15, popMs: 140 } as const
// ♡の生まれる所 = 花嫁のあごの下・組んだ腕の上 (抱く絵の不透明な範囲の中の割合・左上が 0)。
//   10-27 作者「ハートがLUNAIREから出ているのはおかしい、花嫁から出して」で主人公の胸から花嫁へ移した。
//   案A の抱く絵 embrace_hug_1023_art_1025_a の顔 (300,180) と組んだ腕 (330,330) の間 (300,255) を目で取った。
export const BRIDE_HUG_HEART_ORIGIN: readonly [number, number] = [0.25, 0.19]
// 1つずつのずれ (画面の px・dx は絵の向きに合わせて左右を返す)。
const HEART_SPOTS = [{ dx: -6, up: 0 }, { dx: 9, up: 8 }, { dx: 0, up: -8 }] as const

let on = false
let drift: number | undefined
export function brideHugBindOn(): boolean { return on }

export function setBrideHugBind(raw: string | null, dev: boolean = import.meta.env.DEV): void {
  const next = !(dev && raw === 'old')
  const atk = ATTACKS['vowEmbrace']!
  if (next === on) return
  on = next
  if (on) {
    drift = atk.recoveryDriftPx
    ATTACKS['vowEmbrace'] = { ...atk, grab: { ...BRIDE_HUG_GRAB }, recoveryDriftPx: 0 }
  } else {
    const { grab: _g, ...rest } = atk
    ATTACKS['vowEmbrace'] = { ...rest, recoveryDriftPx: drift }
  }
  heartsFrom = null
  heartOrigin = null
}

// 抱いた時刻 (秒)。放した後も♡を消え切るまで描く。
let heartsFrom: number | null = null
let wasHeld = false
// 抱く絵が出ている間の♡の生まれる所。放して絵が替わっても、浮いている♡はそこから動かさない。
let heartOrigin: { x: number; y: number; flip: number } | null = null

// 配布版の既定 = 抱きとめる (URL を読む前から)。
setBrideHugBind(null)

/** 抱擁の♡ (主人公を描いた後・腕の前に)。pl = いま描いた花嫁の体の置き場所。 */
export function drawBrideHugHearts(
  ctx: CanvasRenderingContext2D, bossId: string | undefined, player: Player, time: number, pl: BossSpritePlacement | null,
): void {
  if (!on || bossId !== 'bride') return
  if (pl && pl.imageKey.includes('embrace_hug_1023')) {
    const p = projectBossSpriteRelativePoint(pl, BRIDE_HUG_HEART_ORIGIN)
    heartOrigin = { x: p.x, y: p.y, flip: pl.facingMul }
  }
  const held = player.state === 'grabbed'
  if (held && !wasHeld) heartsFrom = time - player.stateElapsed
  wasHeld = held
  if (heartsFrom === null) return
  const ms = (time - heartsFrom) * 1000
  const H = BRIDE_HUG_HEART
  if (ms < 0 || ms > H.birthMs[H.birthMs.length - 1]! + H.lifeMs) { if (!held) heartsFrom = null; return }
  const o = heartOrigin
  if (!o) return
  for (let k = 0; k < H.birthMs.length; k++) {
    const t = (ms - H.birthMs[k]!) / H.lifeMs
    if (t < 0 || t > 1) continue
    const spot = HEART_SPOTS[k]!
    const rise = 1 - (1 - t) * (1 - t)
    const x = o.x + spot.dx * o.flip + Math.sin(t * Math.PI * 1.6 + k) * 5
    const y = o.y - spot.up - H.risePx * rise
    const pop = Math.min(1, (ms - H.birthMs[k]!) / H.popMs)
    const scale = 0.4 + 0.6 * (1 - (1 - pop) * (1 - pop)) + 0.08 * Math.sin(pop * Math.PI)
    const alpha = t < 0.6 ? 1 : 1 - (t - 0.6) / 0.4
    drawHeart(ctx, x, y, H.sizePx * scale, alpha, k + 3)
  }
}
